import { Component, lazy, Suspense, useState, type ReactNode } from 'react';

import { useMediaQuery } from '@/shared/mobile';

import type { GhostFibersProps } from './ghost-fibers';

// O WebGL e o `ogl` chegam sob demanda, num chunk próprio (`ghost-fibers-*.js`, teto no .size-limit.json).
const GhostFibers = lazy(() => import('./ghost-fibers'));

/**
 * Parâmetros do Leon (06/10/2026), todos, inclusive a cor. Exceção à regra "cor só por token": é a arte que o
 * Leon escolheu, e o accent do escuro (salmão) deixava as fibras rosadas.
 */
const LOGIN_FIBERS = {
  lineColor: '#F97316',
  glowColor: '#ca9068',
  speed: 0.01,
  scale: 2,
  rotation: -109,
  rotationSpeed: -0.01,
  layers: 2,
  waveAmplitude: 0,
  waveFrequency: 1.5,
  waveSpeed: 1.2,
  layerSpeed: 0.08,
  twist: 0,
  twistFrequency: 6.5,
  twistSpeed: 2.05,
  lineFrequency: 10,
  lineSpacing: 1.85,
  lineSharpness: 13.25,
  glowFalloff: 12,
  glowIntensity: 1.6,
  brightness: 2,
  blueBoost: 1.15,
  vignette: 0.85,
  grain: 0,
  dpr: 1,
  lightMode: false,
  fps: 60,
} as const satisfies GhostFibersProps;

const DESKTOP = '(min-width: 1024px)';
const REDUCED = '(prefers-reduced-motion: reduce)';

type FibersState = 'loading' | 'running' | 'paused' | 'unavailable';

/** Falha ao baixar o chunk (rede): some sem erro na tela; fica o fundo do Lume. */
class FibersBoundary extends Component<{ readonly onError: () => void; readonly children: ReactNode }, { readonly failed: boolean }> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch() {
    this.props.onError();
  }

  override render() {
    return this.state.failed ? null : this.props.children;
  }
}

/**
 * Fundo de fibras do login: só no desktop (≥1024 px), em tela cheia atrás de tudo (inclusive do vidro do
 * painel), com o gradiente de leitura por cima (`lume.css`). Com `prefers-reduced-motion`, um quadro
 * parado; sem WebGL 2, nada (vale o fundo do Lume). `data-state` diz o que aconteceu, para os testes e para a transição de entrada.
 */
export function AuthFibers() {
  const desktop = useMediaQuery(DESKTOP);
  const reduced = useMediaQuery(REDUCED);
  const [state, setState] = useState<FibersState>('loading');
  if (!desktop || state === 'unavailable') return null;
  const unavailable = () => setState('unavailable');
  const shown: FibersState = state === 'running' && reduced ? 'paused' : state;
  return (
    <div aria-hidden data-slot="auth-fibers" data-state={shown} className="uyzy-auth-fibers pointer-events-none fixed inset-0 -z-1">
      <FibersBoundary onError={unavailable}>
        <Suspense fallback={null}>
          <GhostFibers {...LOGIN_FIBERS} paused={reduced} onReady={() => setState('running')} onUnavailable={unavailable} />
        </Suspense>
      </FibersBoundary>
    </div>
  );
}
